import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { CustomizedSwitch, Loader, SelectTypeAutocomplete } from 'components';
import { httpRequestsOnErrorsActions, systemActions } from 'store';
import { FindErrorItem, FindLoad } from 'utils';
import { findGoalOrderOption, goalOrderOptions } from 'fragments/client/clientSingle/clientClinical/goals/constants';

const GET_ACTION = 'GET_COMPANY';
const UPDATE_ACTION = 'UPDATE_CLINICAL_SETTINGS';

export const ClinicalSetupSettings = () => {
    const dispatch = useDispatch();
    const company = useSelector((state) => state.system.company);
    const loading = FindLoad(GET_ACTION);
    const updating = FindLoad(UPDATE_ACTION);
    const [includeAllActiveGoals, setIncludeAllActiveGoals] = useState(false);
    const [goalOrderId, setGoalOrderId] = useState(goalOrderOptions[0].id);
    const updateError = FindErrorItem(UPDATE_ACTION);

    const syncFromCompany = () => {
        setIncludeAllActiveGoals(!!company?.clinicalSettings?.includeAllActiveGoalsInSessions);
        setGoalOrderId(findGoalOrderOption(company?.clinicalSettings?.goalSortType, company?.clinicalSettings?.goalOrderType).id);
    };

    useEffect(() => {
        dispatch(systemActions.getCompany());
    }, []);

    useEffect(() => {
        syncFromCompany();
    }, [company]);

    // The toggle and select change before the request ends; if it fails (the saga toasts the error),
    // show the saved value again.
    useEffect(() => {
        if (!updateError) return;
        syncFromCompany();
        dispatch(httpRequestsOnErrorsActions.removeError(UPDATE_ACTION));
    }, [updateError]);

    /** Default goal order for every goal list (library, client goals, session picker, mobile). */
    const handleGoalOrderChange = (id) => {
        const option = goalOrderOptions.find((o) => o.id === id);
        if (!option || id === goalOrderId || updating?.length) return;
        setGoalOrderId(id);
        dispatch(
            systemActions.updateClinicalSettings({ goalSortType: option.sortType, goalOrderType: option.orderType })
        );
    };

    const handleToggle = () => {
        if (updating?.length) return;
        const next = !includeAllActiveGoals;
        setIncludeAllActiveGoals(next);
        dispatch(systemActions.updateClinicalSettings({ includeAllActiveGoalsInSessions: next }));
    };

    if (loading?.length) {
        return <Loader  />;
    }

    return (
        <div className="supervision-settings-wrapper">
            <div className="supervision-section">
                <div style={{ marginBottom: 12 }}>
                    <p className="settings-subsection-title">Session Configuration</p>
                    <p className="settings-subsection-desc" style={{ margin: 0 }}>
                        Defaults applied when a therapist starts a data collection session.
                    </p>
                </div>

                <div className="clinical-setup-toggle-row">
                    <div>
                        <p className="settings-subsection-title">Include all active goals/targets in all sessions</p>
                        <p className="settings-subsection-desc" style={{ margin: 0 }}>
                            When enabled, all active goals and targets are automatically included when starting a data collection
                            session and cannot be unselected.
                        </p>
                    </div>
                    <CustomizedSwitch
                        checkedBgColor={'#0C66E4'}
                        checked={includeAllActiveGoals}
                        handleClick={handleToggle}
                        disabled={!!updating?.length}
                    />
                </div>
            </div>

            <div className="supervision-section">
                <div style={{ marginBottom: 12 }}>
                    <p className="settings-subsection-title">Goal Lists</p>
                    <p className="settings-subsection-desc" style={{ margin: 0 }}>
                        How goals are ordered across the platform.
                    </p>
                </div>

                <div className="clinical-setup-toggle-row">
                    <div>
                        <p className="settings-subsection-title">Default goal order</p>
                        <p className="settings-subsection-desc" style={{ margin: 0 }}>
                            Sets the default order of goals across the Goal Library, Client Goals,
                            and session goal lists on web and mobile. Users can change the order when needed.
                        </p>
                    </div>
                    <div style={{ width: 220, flexShrink: 0 }}>
                        <SelectTypeAutocomplete
                            disableClearable={true}
                            name={'goalOrder'}
                            handleSelect={(event) => handleGoalOrderChange(event.target.value)}
                            defaultValue={goalOrderId}
                            list={goalOrderOptions}
                            renderValue={(i) => i?.name}
                            uiType={'tableFilter'}
                            noError={true}
                            disabled={!!updating?.length}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};
